import React from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import classNames from 'classnames';

import { Button } from 'uikit/buttons';
import { Grid, Row, Column } from 'uikit/grids';
import { Skeleton } from 'uikit/loaders';
import { Tooltip } from 'uikit/tooltips';
import { Typo } from 'uikit/content';

import { CardChallenge, CardImage } from '@/exceed';

import styles from '../styles/profileChallengesList.module.scss';

const ProfileChallengesList = ({
  activeChallenges = [],
  completedChallenges = [],
  isCurrentUser = false,
  isLoading = true,
  isMyLearning = false,
  onActiveChallengesClick = () => {},
}) => {
  const placeholder = (className) => (
    <div className={classNames(styles.placeholder, className)}>
      <Typo element="h2" variant="d02">
        {i18n.t('find-next-challenge')}
      </Typo>
      <Typo color="secondary" element="p" variant="b01" className={styles.placeholderDescription}>
        {i18n.t('find-next-challenge-description')}
      </Typo>
      <Button text={i18n.t('explore-challenges')} to="/student/challenges" isExternalLink />
    </div>
  );

  const rowSizes = isMyLearning ? { default: 1 } : { default: 1, large: 2 };

  if (isLoading) {
    return (
      <Grid>
        <Row sizes={rowSizes}>
          <Column>
            <div className={styles.active}>
              <Skeleton height={152} width="100%" />
            </div>
          </Column>
          <Column>
            <Skeleton height={152} width="100%" />
          </Column>
        </Row>
      </Grid>
    );
  }

  const completedChallengeRowSizes = isMyLearning
    ? { default: 3, small: 4, medium: 4, large: 4 }
    : { default: 3, small: 4, medium: 4, large: 4, xlarge: 5 };

  // UI for logged in user
  if (isCurrentUser && (!!activeChallenges?.length || !!completedChallenges?.length)) {
    return (
      <Grid>
        <Row sizes={rowSizes}>
          <Column>
            <Typo element="h3" variant="s03" weight="medium">
              {i18n.t('active-challenges')}
            </Typo>
            {activeChallenges?.length ? (
              <>
                <div className={styles.spacing}>
                  <Grid>
                    <Row sizes={rowSizes}>
                      {activeChallenges.map((challenge) => (
                        <Column key={challenge.challenge_id}>
                          <CardChallenge
                            challengeType={challenge.challenge_type}
                            endsAt={challenge.ends_at}
                            image={challenge.challenge_picture.w96}
                            name={challenge.name}
                            size="large"
                            startsAt={challenge.starts_at}
                            to={`/student/challenges/${challenge.challenge_id}`}
                            hasJoinedChallenge
                            isExternalLink
                          />
                        </Column>
                      ))}
                    </Row>
                  </Grid>
                </div>
                <Button
                  text={i18n.t('explore-challenges')}
                  to="/student/challenges"
                  variant="outlined"
                  isExternalLink
                />
              </>
            ) : (
              <div className={styles.spacing}>{placeholder(styles.outlined)}</div>
            )}
          </Column>
          {!!completedChallenges?.length && (
            <Column>
              <Typo element="h3" variant="s03" weight="medium">
                {i18n.t('completed-challenges')}
              </Typo>
              <div className={styles.spacing}>
                <Grid>
                  <Row sizes={completedChallengeRowSizes}>
                    {completedChallenges.map((challenge) => (
                      <Column key={challenge.challenge_id}>
                        <Tooltip content={challenge.name}>
                          <CardImage
                            image={challenge.challenge_picture.w96}
                            onClick={() => onActiveChallengesClick(challenge.challenge_id)}
                          />
                        </Tooltip>
                      </Column>
                    ))}
                  </Row>
                </Grid>
              </div>
              <Button
                text={i18n.t('view-past-challenges')}
                to="/student/challenges?filter=past"
                variant="outlined"
                isExternalLink
              />
            </Column>
          )}
        </Row>
      </Grid>
    );
  }

  if (completedChallenges?.length) {
    return (
      <>
        <Typo element="h3" variant="s03" weight="medium">
          {i18n.t('completed-challenges')}
        </Typo>
        <div className={styles.spacing}>
          <Grid>
            <Row
              sizes={{
                default: 3,
                small: 6,
                medium: 8,
                xlarge: 10,
              }}
            >
              {completedChallenges.map((challenge) => (
                <Column key={challenge.challenge_id}>
                  <Tooltip content={challenge.name}>
                    <CardImage image={challenge.challenge_picture.w96} imageAlt={challenge.name} />
                  </Tooltip>
                </Column>
              ))}
            </Row>
          </Grid>
        </div>
        <Button
          text={i18n.t('view-past-challenges')}
          to="/student/challenges?filter=past"
          variant="outlined"
          isExternalLink
        />
      </>
    );
  }

  return placeholder();
};

ProfileChallengesList.propTypes = {
  activeChallenges: PropTypes.arrayOf(
    PropTypes.shape({
      challenge_id: PropTypes.number,
      name: PropTypes.string,
      challenge_picture: PropTypes.shape({
        w96: PropTypes.string,
      }),
    }),
  ),
  completedChallenges: PropTypes.arrayOf(
    PropTypes.shape({
      challenge_id: PropTypes.number,
      name: PropTypes.string,
      challenge_picture: PropTypes.shape({
        w96: PropTypes.string,
      }),
    }),
  ),
  isCurrentUser: PropTypes.bool,
  isLoading: PropTypes.bool,
  isMyLearning: PropTypes.bool,
  onActiveChallengesClick: PropTypes.func,
};

export default React.memo(ProfileChallengesList);
